import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { Link } from "react-router-dom";
import {
  ArrowDownLeft,
  ArrowUpRight,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Pencil,
  Wallet,
} from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import {
  occurrences,
  periodRange,
  calendarRange,
  monthAdd,
  dayAdd,
  type PlanItem,
  type PlanEvent,
} from "../../../../convex/lib/moneyPlan";
import { formatMoney } from "@/lib/utils";
import { availableCashCoverage } from "@/lib/cashCoverage";
import "../styles/money.css";
import "../styles/money-pages.css";
import {
  paymentId,
  paymentReserve,
  fundingLabel,
} from "@/lib/paymentSelection";

const dateLabel = (
  date: string,
  options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" },
) => new Date(`${date}T12:00:00`).toLocaleDateString("en-US", options);
const sourceDateLabel = (value: string) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return dateLabel(value);
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime())
    ? parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/Los_Angeles" })
    : "Date unverified";
};
const todayDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const timestamp = (value: number) =>
  new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
type PlanData = NonNullable<
  ReturnType<typeof useQuery<typeof api.money.getPlan>>
>;

export function UpcomingPage() {
  const data = useQuery(api.money.getPlan);
  const check = useMutation(api.money.checkEvent);
  const today = data?.today ?? todayDate();
  const [anchor, setAnchor] = useState(today);
  const [view, setView] = useState<"month" | "week">("month");
  const [selected, setSelected] = useState<string | null>(null);
  const [account, setAccount] = useState("all");
  const [editing, setEditing] = useState<PlanItem | null>(null);
  const [error, setError] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [selectionRange, setSelectionRange] = useState({
    start: today,
    end: today,
  });
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const dragStart = useRef<string | null>(null);
  useEffect(() => {
    const stop = () => {
      dragStart.current = null;
    };
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    window.addEventListener("blur", stop);
    return () => {
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      window.removeEventListener("blur", stop);
    };
  }, []);
  const selectRange = (start: string, end: string) => {
    setSelectionRange({
      start: start < end ? start : end,
      end: start < end ? end : start,
    });
    setExcluded(new Set());
  };
  const reserveEvents = data
    ? occurrences(
        data.items,
        selectionRange.start,
        selectionRange.end,
        new Set(data.completed),
      )
    : [];
  const reserve = paymentReserve(
    reserveEvents.filter((e) => account === "all" || e.accountId === account),
    excluded,
  );
  const paydayEvents = data
    ? occurrences(
        data.items,
        monthAdd(`${anchor.slice(0, 7)}-01`, -1),
        monthAdd(`${anchor.slice(0, 7)}-01`, 2),
      ).filter((e) => e.kind === "income")
    : [];
  const paydayDates = [...new Set(paydayEvents.map((e) => e.eventDate))];
  const range = periodRange(anchor, view);
  const { start: calendarStart, end: calendarEnd } = calendarRange(anchor);
  const allEvents = useMemo(
    () =>
      data
        ? occurrences(
            data.items,
            view === "month" ? calendarStart : range.start,
            view === "month" ? calendarEnd : range.end,
            new Set(data.completed),
          )
        : [],
    [data, range.start, range.end, calendarStart, calendarEnd, view],
  );
  const events = allEvents.filter(
    (e) => account === "all" || e.accountId === account,
  );
  const upcoming = data
    ? occurrences(
        data.items,
        today,
        dayAdd(today, 35),
        new Set(data.completed),
      ).filter((e) => !e.completed)
    : [];
  const nextIncome = upcoming.find((e) => e.kind === "income");
  const weekPayments = upcoming.filter(
    (e) => e.kind === "payment" && e.eventDate <= dayAdd(today, 6),
  );
  const cashCoverage = availableCashCoverage(data?.accounts ?? [], Date.now());
  const totalCash = cashCoverage.total;
  const unknown =
    data?.items.filter((i) =>
      i.active && (!i.date || !i.accountId) &&
      !(i.cadence === "once" && data.completed.includes(`${i.key}:${i.date}`))
    ) ?? [];
  const agenda = selected
    ? events.filter((e) => e.eventDate === selected)
    : events.filter(
        (e) => e.eventDate >= range.start && e.eventDate <= range.end,
      );
  const grouped = [...new Set(agenda.map((e) => e.eventDate))];
  const changePeriod = (direction: number) => {
    setAnchor(
      view === "month"
        ? monthAdd(`${anchor.slice(0, 7)}-01`, direction)
        : dayAdd(anchor, direction * 7),
    );
    setSelected(null);
  };
  const add = () =>
    setEditing({
      key: `manual:${crypto.randomUUID()}`,
      name: "",
      amount: 0,
      kind: "payment",
      date: today,
      cadence: "monthly",
      accountId: null,
      accountLabel: "",
      estimated: false,
      active: true,
    });
  if (!data)
    return (
      <div className="money-page" role="status">
        <div className="money-loading" />
        Loading your money…
      </div>
    );
  return (
    <div className="money-page">
      <div className="money-heading">
        <div>
          <p className="eyebrow">Your money, in order</p>
          <h1>What’s coming up</h1>
          <p>Due dates, planned payments, and paydays in one place.</p>
        </div>
        <button className="money-primary" onClick={add}>
          <Plus size={16} /> Add payment or payday
        </button>
      </div>
      <section className="money-stats" aria-label="Money at a glance">
        <div>
          <span>Available cash</span>
          <strong className={totalCash === null ? "cash-unknown" : ""}>
            {totalCash === null ? "Needs verification" : formatMoney(totalCash)}
          </strong>
          <small>
            {cashCoverage.coveredCount} of {data.accounts.length} accounts have
            a recent available balance
          </small>
        </div>
        <div>
          <span>Payments in the next 7 days</span>
          <strong>
            {formatMoney(weekPayments.reduce((s, e) => s + e.amount, 0))}
          </strong>
          <small>
            {weekPayments.length} unpaid scheduled payment
            {weekPayments.length === 1 ? "" : "s"}
          </small>
        </div>
        <div>
          <span>Next payday</span>
          <strong className="income">
            {nextIncome ? formatMoney(nextIncome.amount) : "Add your payday"}
          </strong>
          <small>
            {nextIncome
              ? `${dateLabel(nextIncome.eventDate)} · ${nextIncome.accountLabel || "Choose destination"}${nextIncome.estimated ? " · estimate" : ""}`
              : "Set the amount, frequency, and destination account"}
          </small>
        </div>
      </section>
      <section className="account-strip" aria-label="Your accounts">
        {data.accounts.length === 0 && (
          <p>
            No cash accounts are mapped yet.{" "}
            <Link to="/settings">Review account connections</Link>.
          </p>
        )}
        {data.accounts.map((a) => {
          let running = a.available ?? a.balance;
          let low = running;
          let lowDate = today;
          // Same-day income is not assumed to settle before a debit.
          const future = upcoming
            .filter((e) => e.accountId === a.id)
            .sort(
              (a, b) =>
                a.eventDate.localeCompare(b.eventDate) ||
                (a.kind === "payment" ? -1 : 1),
            );
          for (const e of future) {
            running += e.kind === "income" ? e.amount : -e.amount;
            if (running < low) {
              low = running;
              lowDate = e.eventDate;
            }
          }
          const bankTimestamp = a.bankObservedAt
            ? Date.parse(a.bankObservedAt)
            : null;
          const stale =
            !bankTimestamp || Date.now() - bankTimestamp > 75 * 60000;
          return (
            <button
              key={a.id}
              className={`account-card ${account === a.id ? "chosen" : ""}`}
              onClick={() => {
                setAccount(account === a.id ? "all" : a.id);
                setSelected(null);
              }}
              aria-pressed={account === a.id}
            >
              <span>
                <Wallet size={15} />
                {a.name}
              </span>
              <strong>{formatMoney(a.balance)}</strong>
              <small>
                {stale
                  ? "Historical balance · needs refresh"
                  : `${a.balanceKind} balance`}
              </small>
              {!stale && a.available != null ? (
                <small>Available {formatMoney(a.available)}</small>
              ) : (
                <small>Recent available balance unverified</small>
              )}
              <small className={stale || a.bankError ? "warning-text" : ""}>
                {a.bankError
                  ? "Bank connection needs attention"
                  : `${a.balanceSource}${bankTimestamp ? ` · bank date ${timestamp(bankTimestamp)}` : ` · fetched ${timestamp(a.updatedAt)}; bank date unavailable`}`}
              </small>
              {a.balanceKind === "Bank-reported current" &&
                Math.abs(a.balance - a.ynabBalance) >= 0.01 && (
                  <small>Stored ledger: {formatMoney(a.ynabBalance)}</small>
                )}
              {!stale && a.available != null && low < 0 && (
                <small className="warning-text">
                  Planned shortfall {formatMoney(-low)} · {dateLabel(lowDate)}
                </small>
              )}
            </button>
          );
        })}
      </section>
      <p className="money-note">
        Current and ledger balances are not available cash. Available cash stays
        unknown until every account has a bank timestamp and available balance
        from the last 75 minutes. Credit limits and expected paydays are not
        cash. The calendar is a plan; it does not send payments or verify
        settlement.
      </p>
      {error && (
        <p role="alert" className="money-error">
          {error}
        </p>
      )}
      <div className="calendar-coverage" role="note">
        <div>
          <strong>Recorded payment plan</strong>
          <span>
            {data.calendarReviewedAt
              ? `Reviewed ${timestamp(data.calendarReviewedAt)}`
              : "No organizer review published yet"}
          </span>
        </div>
        <p>
          {data.coverageNote ??
            "Recorded schedules only. Review dates, funding, and amounts before making a payment."}{" "}
          {unknown.length
            ? `${unknown.length} schedules need a date or funding account.`
            : ""}{" "}
          <Link to="/agent">Open the finance brief</Link>
        </p>
      </div>
      <section className="money-calendar-section">
        <div className="calendar-toolbar">
          <div className="calendar-title">
            <CalendarDays size={19} />
            <h2>{dateLabel(anchor, { month: "long", year: "numeric" })}</h2>
            <div className="period-buttons">
              <button
                onClick={() => changePeriod(-1)}
                aria-label="Previous period"
              >
                <ChevronLeft size={17} />
              </button>
              <button
                onClick={() => {
                  setAnchor(today);
                  setSelected(null);
                }}
              >
                Today
              </button>
              <button onClick={() => changePeriod(1)} aria-label="Next period">
                <ChevronRight size={17} />
              </button>
            </div>
          </div>
          <div className="calendar-options">
            <select
              aria-label="Filter account"
              value={account}
              onChange={(e) => {
                setAccount(e.target.value);
                setSelected(null);
              }}
            >
              <option value="all">All accounts</option>
              {data.accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <div className="money-segments">
              {(["month", "week"] as const).map((v) => (
                <button
                  key={v}
                  aria-pressed={view === v}
                  onClick={() => {
                    setView(v);
                    setSelected(null);
                  }}
                >
                  {v === "month" ? "Month" : "Week"}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="reserve-toolbar">
          <button
            className="reserve-toggle"
            aria-pressed={selecting}
            onClick={() => {
              setSelecting(!selecting);
              setSelected(null);
              dragStart.current = null;
            }}
          >
            {selecting ? "Done selecting" : "Select payments"}
          </button>
          <span>
            Choose a date range to total unpaid payments. Drag on desktop, or
            use the date fields below.
          </span>
          <strong role="status" aria-live="polite">
            {selecting ? `${formatMoney(reserve.total)} to keep` : ""}
          </strong>
          {selecting && (
            <button
              onClick={() =>
                document.getElementById("payment-selection")?.scrollIntoView({
                  behavior: window.matchMedia(
                    "(prefers-reduced-motion: reduce)",
                  ).matches
                    ? "auto"
                    : "smooth",
                  block: "start",
                })
              }
            >
              Paycheck window & bank breakdown ↓
            </button>
          )}
        </div>
        {view === "month" && (
          <div className="calendar-grid" aria-label="Monthly payment calendar">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div className="weekday" key={d}>
                {d}
              </div>
            ))}
            {Array.from(
              {
                length:
                  Math.round(
                    (Date.parse(calendarEnd) - Date.parse(calendarStart)) /
                      86400000,
                  ) + 1,
              },
              (_, i) => {
                const date = dayAdd(calendarStart, i);
                const outsideMonth = date.slice(0, 7) !== anchor.slice(0, 7);
                const dayEvents = events.filter((e) => e.eventDate === date);
                return (
                  <button
                    key={date}
                    className={`calendar-day ${outsideMonth ? "outside-month" : ""} ${selecting ? "selection-enabled" : ""} ${date === today ? "is-today" : ""} ${(selecting ? date >= selectionRange.start && date <= selectionRange.end : selected === date) ? "is-selected" : ""}`}
                    onPointerDown={(e) => {
                      if (
                        selecting &&
                        e.button === 0 &&
                        e.pointerType === "mouse"
                      ) {
                        e.preventDefault();
                        dragStart.current = date;
                        selectRange(date, date);
                      }
                    }}
                    onPointerEnter={() => {
                      if (selecting && dragStart.current)
                        selectRange(dragStart.current, date);
                    }}
                    onPointerUp={(e) => {
                      if (selecting && e.pointerType !== "mouse")
                        selectRange(date, date);
                    }}
                    onClick={(e) => {
                      if (selecting) {
                        if (e.detail === 0) selectRange(date, date);
                      } else setSelected(selected === date ? null : date);
                    }}
                    aria-label={`${date === today ? "Today, " : ""}${dateLabel(date, { weekday: "long", month: "long", day: "numeric" })}: ${dayEvents.length ? dayEvents.map((e) => `${e.name}, ${formatMoney(e.amount)}${e.completed ? ", marked complete" : ""}`).join("; ") : "No scheduled items"}`}
                    aria-pressed={
                      selecting
                        ? date >= selectionRange.start &&
                          date <= selectionRange.end
                        : selected === date
                    }
                  >
                    <span className="day-number">
                      {outsideMonth ? dateLabel(date) : Number(date.slice(-2))}
                      {date === today && (
                        <span className="today-label">Today</span>
                      )}
                    </span>
                    <div className="calendar-chips">
                      {dayEvents.slice(0, 2).map((e) => (
                        <span
                          key={e.key}
                          className={`calendar-chip ${e.kind} ${e.completed ? "completed" : ""}`}
                        >
                          <span>{e.name}</span>
                          <b>{formatMoney(e.amount)}</b>
                          <small>{fundingLabel(e)}</small>
                        </span>
                      ))}
                      {dayEvents.length > 2 && (
                        <small>+{dayEvents.length - 2} more</small>
                      )}
                    </div>
                    <span className="mobile-event-count" aria-hidden="true">
                      {dayEvents.length
                        ? `${dayEvents.length} item${dayEvents.length === 1 ? "" : "s"}`
                        : ""}
                    </span>
                  </button>
                );
              },
            )}
          </div>
        )}
        {selecting && (
          <section
            id="payment-selection"
            className="payment-reserve"
            aria-label="Payment selection"
          >
            <div className="reserve-controls">
              <label>
                Start date
                <input
                  type="date"
                  aria-label="Start date"
                  value={selectionRange.start}
                  onInput={(e) => {
                    if (e.currentTarget.value)
                      selectRange(e.currentTarget.value, selectionRange.end);
                  }}
                />
              </label>
              <label>
                End date
                <input
                  type="date"
                  aria-label="End date"
                  value={selectionRange.end}
                  onInput={(e) => {
                    if (e.currentTarget.value)
                      selectRange(selectionRange.start, e.currentTarget.value);
                  }}
                />
              </label>
              <label>
                Between paychecks
                <select
                  aria-label="Between paychecks"
                  value=""
                  onChange={(e) => {
                    const i = Number(e.target.value);
                    if (e.target.value !== "")
                      selectRange(paydayDates[i], paydayDates[i + 1]);
                  }}
                >
                  <option value="">Choose a paycheck window</option>
                  {paydayDates.slice(0, -1).map((date, i) => (
                    <option key={date} value={i}>
                      {dateLabel(date)} – {dateLabel(paydayDates[i + 1])}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="reserve-total">
              <span>Keep for {reserve.selected.length} selected payments</span>
              <strong>{formatMoney(reserve.total)}</strong>
            </div>
            <p>
              Both dates included. Payments on the next payday are included
              because debits may settle before your paycheck. Income is not
              subtracted; already-paid items are excluded. This covers scheduled
              payments only, not all living expenses. Unlinked bank names are
              saved schedule labels, not verified account matches.
            </p>
            {account !== "all" && (
              <p className="warning-text">
                Filtered to one account. Choose All accounts to include other
                banks and unassigned payments.
              </p>
            )}
            <div className="reserve-banks">
              {reserve.banks.map((b) => (
                <div
                  key={b.label}
                  className={b.unassigned ? "warning-text" : ""}
                >
                  <span>
                    {b.label} · {b.count}
                  </span>
                  <strong>{formatMoney(b.cents / 100)}</strong>
                </div>
              ))}
            </div>
            {reserve.payments.length === 0 ? (
              <p>No unpaid scheduled payments in this selection.</p>
            ) : (
              <details open>
                <summary>Adjust selected payments</summary>
                <div className="reserve-items">
                  {reserve.payments.map((e) => (
                    <label key={paymentId(e)}>
                      <input
                        type="checkbox"
                        checked={!excluded.has(paymentId(e))}
                        onChange={() =>
                          setExcluded((previous) => {
                            const next = new Set(previous);
                            const id = paymentId(e);
                            if (next.has(id)) next.delete(id);
                            else next.add(id);
                            return next;
                          })
                        }
                      />
                      <span>
                        {dateLabel(e.eventDate)} · {e.name}
                        <small>
                          {fundingLabel(e)}
                          {e.estimated ? " · Estimated" : ""}
                        </small>
                      </span>
                      <b>{formatMoney(e.amount)}</b>
                    </label>
                  ))}
                </div>
              </details>
            )}
          </section>
        )}
        <div className="agenda-heading">
          <h3>
            {selected
              ? dateLabel(selected, {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })
              : view === "week"
                ? `${dateLabel(range.start)} – ${dateLabel(range.end)}`
                : "This month’s schedule"}
          </h3>
          {selected && (
            <button onClick={() => setSelected(null)}>Show all dates</button>
          )}
          <span>
            <i className="legend income" />
            Income <i className="legend payment" />
            Payment
          </span>
        </div>
        {agenda.length === 0 ? (
          <div className="money-empty">
            <CalendarDays size={28} />
            <h3>Nothing scheduled here</h3>
            <p>Add your bills and paydays to see them together.</p>
            <button onClick={add}>Add an item</button>
          </div>
        ) : (
          grouped.map((date) => (
            <div className="agenda-group" key={date}>
              <div className="agenda-date">
                <strong>{dateLabel(date, { day: "numeric" })}</strong>
                <span>{dateLabel(date, { weekday: "short" })}</span>
              </div>
              <div className="agenda-items">
                {agenda
                  .filter((e) => e.eventDate === date)
                  .map((e) => (
                    <div
                      className={`agenda-item ${e.completed ? "done" : ""}`}
                      key={e.key}
                    >
                      <button
                        className={`payment-check ${e.completed ? "checked" : ""}`}
                        aria-label={
                          e.source && e.completed
                            ? `${e.name} recorded complete in the finance review`
                            : `${e.completed ? "Unmark" : "Mark"} ${e.name} ${e.kind === "income" ? "received" : "paid"}`
                        }
                        aria-pressed={e.completed}
                        disabled={
                          pending === `${e.key}:${date}` ||
                          (!!e.source && e.completed)
                        }
                        onClick={async () => {
                          setPending(`${e.key}:${date}`);
                          setError("");
                          try {
                            await check({
                              key: e.key,
                              date,
                              completed: !e.completed,
                            });
                          } catch (err) {
                            setError(
                              err instanceof Error
                                ? `${err.message} Check your connection and try again.`
                                : "Unable to save this status. Check your connection and try again.",
                            );
                          } finally {
                            setPending(null);
                          }
                        }}
                      >
                        {e.completed && <Check size={13} />}
                      </button>
                      <div className={`event-icon ${e.kind}`}>
                        {e.kind === "income" ? (
                          <ArrowDownLeft size={18} />
                        ) : (
                          <ArrowUpRight size={18} />
                        )}
                      </div>
                      <div className="event-details">
                        <strong>{e.name}</strong>
                        <span>
                          {e.accountLabel || "Account not assigned"} ·{" "}
                          {e.completed
                            ? e.source
                              ? "Recorded complete"
                              : e.kind === "income"
                                ? "Marked received"
                                : "Marked paid"
                            : e.source && e.name.includes("scheduled draft")
                              ? "Scheduled draft · do not repeat"
                            : e.source && e.name.includes("AutoPay scheduled")
                              ? "AutoPay scheduled"
                            : !e.source && e.key.startsWith("loan:")
                              ? "Saved loan schedule"
                              : e.estimated
                                ? "Estimate · verify amount"
                                : "Planned"}
                          {!e.completed && date < today
                            ? " · Check status"
                            : ""}
                        </span>
                        {e.note && <details className="event-evidence"><summary>Payment details</summary><p>{e.note}</p>{e.source && <p>Source: {e.source}</p>}</details>}
                        {e.observedAt && (
                          <small className="event-evidence">
                            Source date {sourceDateLabel(e.observedAt)}
                          </small>
                        )}
                      </div>
                      <strong className={`event-amount ${e.kind}`}>
                        {e.kind === "income" ? "+" : "−"}
                        {formatMoney(e.amount)}
                      </strong>
                      {e.source ? (
                        <Link
                          to="/agent"
                          className="edit-event"
                          aria-label={`Review ${e.name} evidence`}
                        >
                          <ArrowUpRight size={15} />
                        </Link>
                      ) : (
                        <button
                          className="edit-event"
                          aria-label={`Edit ${e.name}`}
                          onClick={() =>
                            setEditing(data.items.find((i) => i.key === e.key)!)
                          }
                        >
                          <Pencil size={15} />
                        </button>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          ))
        )}
        <p className="money-note agenda-footnote">
          Checking an item records your confirmation. It does not send a payment
          or reconcile a bank transaction.
        </p>
      </section>
      {data.suggestions.length > 0 && (
        <details className="money-attention">
          <summary>
            {data.suggestions.length} recurring item
            {data.suggestions.length === 1 ? "" : "s"} found in recent
            transactions
          </summary>
          <p>
            Review these before adding them. Predictions don’t affect your
            calendar or forecast until saved.
          </p>
          <div className="setup-list">
            {data.suggestions.map((i) => (
              <button key={i.key} onClick={() => setEditing(i)}>
                {i.name}
                <span>
                  {formatMoney(i.amount)} · {i.cadence} <Plus size={14} />
                </span>
              </button>
            ))}
          </div>
        </details>
      )}
      {unknown.length > 0 && (
        <details className="money-attention">
          <summary>
            {unknown.length} schedule{unknown.length === 1 ? "" : "s"} need a
            date or funding account
          </summary>
          <p>
            Unassigned payments aren’t included in an individual account’s
            forecast.
          </p>
          <div className="setup-list">
            {unknown.map((i) =>
              i.source ? (
                <Link key={i.key} to="/agent">
                  {i.name}
                  <span>
                    Review funding and source <ChevronRight size={14} />
                  </span>
                </Link>
              ) : (
                <button key={i.key} onClick={() => setEditing(i)}>
                  {i.name}
                  <span>
                    {!i.date ? "Add due date" : "Choose account"}{" "}
                    <ChevronRight size={14} />
                  </span>
                </button>
              ),
            )}
          </div>
        </details>
      )}
      <details className="money-attention schedule-library">
        <summary>
          Manage saved schedules ·{" "}
          {data.items.filter((i) => i.active && !i.source).length} active
        </summary>
        <div className="setup-list">
          {data.items
            .filter((i) => !i.source)
            .map((i) => (
              <button key={i.key} onClick={() => setEditing(i)}>
                {i.name}
                <span>
                  {!i.active
                    ? "Paused"
                    : i.kind === "income"
                      ? "Income"
                      : "Payment"}{" "}
                  · {formatMoney(i.amount)} <Pencil size={14} />
                </span>
              </button>
            ))}
        </div>
      </details>
      {editing && (
        <ScheduleEditor
          item={editing}
          accounts={data.accounts}
          close={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ScheduleEditor({
  item,
  accounts,
  close,
}: {
  item: PlanItem;
  accounts: PlanData["accounts"];
  close: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const current = dialog.current;
    current?.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      current?.close();
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, []);
  const save = useMutation(api.money.saveSchedule);
  const [value, setValue] = useState({
    ...item,
    date: item.date || todayDate(),
  });
  const [amount, setAmount] = useState(item.amount ? String(item.amount) : "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  return (
    <dialog
      ref={dialog}
      className="money-modal schedule-dialog"
      aria-labelledby="schedule-title"
      onCancel={(e) => {
        e.preventDefault();
        if (!saving) close();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) close();
      }}
    >
      <div className="modal-heading">
        <div>
          <p className="eyebrow">YOUR SCHEDULE</p>
          <h2 id="schedule-title">
            {item.name ? "Edit schedule" : "Add to your calendar"}
          </h2>
        </div>
        <button aria-label="Close editor" onClick={close} disabled={saving}>
          <X size={20} />
        </button>
      </div>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setSaving(true);
          setError("");
          try {
            await save({
              key: value.key,
              name: value.name,
              amount: Number(amount),
              kind: value.kind,
              date: value.date,
              cadence: value.cadence,
              accountId: value.accountId || undefined,
              estimated: value.estimated,
              active: value.active,
            });
            close();
          } catch (err) {
            setError(
              `${err instanceof Error ? err.message : "Unable to save schedule."} Check the fields and try again.`,
            );
          } finally {
            setSaving(false);
          }
        }}
      >
        <div className="money-segments">
          {(["payment", "income"] as const).map((kind) => (
            <button
              type="button"
              key={kind}
              aria-pressed={value.kind === kind}
              onClick={() => setValue({ ...value, kind })}
            >
              {kind === "income" ? "Payday / income" : "Payment / bill"}
            </button>
          ))}
        </div>
        <label>
          Name
          <input
            autoFocus
            required
            maxLength={120}
            name="schedule-name"
            autoComplete="off"
            placeholder="Rent"
            value={value.name}
            onChange={(e) => setValue({ ...value, name: e.target.value })}
          />
        </label>
        <div className="form-row">
          <label>
            Amount ($)
            <input
              required
              type="number"
              name="schedule-amount"
              inputMode="decimal"
              min="0.01"
              max="10000000"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <label>
            First due date
            <input
              required
              type="date"
              name="schedule-date"
              value={value.date}
              onChange={(e) => setValue({ ...value, date: e.target.value })}
            />
          </label>
        </div>
        <label>
          Repeats
          <select
            value={value.cadence}
            onChange={(e) =>
              setValue({
                ...value,
                cadence: e.target.value as PlanItem["cadence"],
              })
            }
          >
            <option value="once">Does not repeat</option>
            <option value="weekly">Every week</option>
            <option value="biweekly">Every two weeks</option>
            <option value="monthly">Every month</option>
          </select>
        </label>
        <label>
          {value.kind === "income" ? "Deposited into" : "Paid from"}
          <select
            value={value.accountId ?? ""}
            onChange={(e) =>
              setValue({ ...value, accountId: e.target.value || null })
            }
          >
            <option value="">Not assigned yet</option>
            {accounts.map((a) => (
              <option value={a.id} key={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={value.estimated}
            onChange={(e) =>
              setValue({ ...value, estimated: e.target.checked })
            }
          />{" "}
          Amount or date is an estimate
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={value.active}
            onChange={(e) => setValue({ ...value, active: e.target.checked })}
          />{" "}
          Active schedule
        </label>
        <p className="money-note">
          This updates your Findash plan. Monthly dates fall on the last day of
          shorter months. Pausing hides all occurrences.
        </p>
        {error && (
          <p className="money-error" role="alert">
            {error}
          </p>
        )}
        <button disabled={saving} className="money-primary" type="submit">
          {saving ? "Saving…" : "Save schedule"}
        </button>
      </form>
    </dialog>
  );
}

export function SpendingPage() {
  const today = todayDate();
  const [period, setPeriod] = useState<"day" | "week" | "month">("month");
  const [anchor, setAnchor] = useState(today);
  const [category, setCategory] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const fullRange = periodRange(anchor, period);
  const end =
    fullRange.end > today && fullRange.start <= today ? today : fullRange.end;
  const previousFull = periodRange(dayAdd(fullRange.start, -1), period);
  const elapsedDays = Math.round(
    (Date.parse(end) - Date.parse(fullRange.start)) / 86400000,
  );
  const previousEnd = [
    dayAdd(previousFull.start, elapsedDays),
    previousFull.end,
  ].sort()[0];
  const data = useQuery(api.money.spending, {
    start: fullRange.start,
    end,
    previousStart: previousFull.start,
    previousEnd,
  });
  const rows = data?.rows ?? [];
  const total = rows.reduce((s, t) => s + t.amount, 0);
  const previous = data?.previous.reduce((s, t) => s + t.amount, 0) ?? 0;
  const categories = [...new Set(rows.map((t) => t.category))]
    .map((name) => ({
      name,
      total: rows
        .filter((t) => t.category === name)
        .reduce((s, t) => s + t.amount, 0),
      count: rows.filter((t) => t.category === name).length,
    }))
    .sort((a, b) => b.total - a.total);
  const visible = rows
    .filter(
      (t) =>
        (!category || t.category === category) &&
        `${t.name} ${t.account} ${t.category}`
          .toLowerCase()
          .includes(search.toLowerCase()),
    )
    .sort((a, b) => b.date.localeCompare(a.date));
  const changePeriod = (n: number) => {
    setAnchor(
      period === "month"
        ? monthAdd(`${anchor.slice(0, 7)}-01`, n)
        : dayAdd(anchor, n * (period === "week" ? 7 : 1)),
    );
    setCategory(null);
  };
  return (
    <div className="money-page">
      <div className="money-heading">
        <div>
          <p className="eyebrow">Spending overview</p>
          <h1>Spending</h1>
          <p>A clear picture of your everyday spending.</p>
        </div>
        <div className="money-segments">
          {(["day", "week", "month"] as const).map((p) => (
            <button
              key={p}
              aria-pressed={period === p}
              onClick={() => {
                setPeriod(p);
                setCategory(null);
              }}
            >
              {p === "day" ? "Daily" : p === "week" ? "Weekly" : "Monthly"}
            </button>
          ))}
        </div>
      </div>
      <div className="spending-period">
        <div className="period-buttons">
          <button
            aria-label="Previous spending period"
            onClick={() => changePeriod(-1)}
          >
            <ChevronLeft size={18} />
          </button>
          <strong>
            {dateLabel(fullRange.start)}
            {end !== fullRange.start ? ` – ${dateLabel(end)}` : ""}
          </strong>
          <button
            aria-label="Next spending period"
            disabled={fullRange.end >= today}
            onClick={() => changePeriod(1)}
          >
            <ChevronRight size={18} />
          </button>
        </div>
        <button
          onClick={() => {
            setAnchor(today);
            setCategory(null);
          }}
        >
          Current {period}
        </button>
      </div>
      {!data ? (
        <p role="status" className="money-empty">
          Loading spending…
        </p>
      ) : (
        <>
          <div className="spending-total">
            <span>
              Spent this {period}
              {end !== fullRange.end ? " so far" : ""}
            </span>
            <strong>{formatMoney(total)}</strong>
            <p>
              {previous === 0
                ? "No spending in the comparison period"
                : `${formatMoney(Math.abs(total - previous))} ${total > previous ? "more" : "less"} than ${dateLabel(previousFull.start)}${previousEnd !== previousFull.start ? ` – ${dateLabel(previousEnd)}` : ""}`}
            </p>
          </div>
          <p className="money-note">
            Net of refunds. Includes pending purchases. Transfers, card
            repayments, and balance adjustments are excluded.{" "}
            {data.sourceLabel ?? "Recorded ledger"}.{" "}
            {data.lastSync
              ? `Oldest account sync: ${timestamp(data.lastSync)}. Bank reporting may lag.`
              : "No sync recorded. Review connections in Settings."}
          </p>
          <section className="spending-columns">
            <div className="category-panel">
              <div className="agenda-heading">
                <h2>By category</h2>
                {category && (
                  <button onClick={() => setCategory(null)}>
                    Clear filter
                  </button>
                )}
              </div>
              {categories.length === 0 && (
                <p className="money-empty">
                  No spending recorded for this period. Choose another period or
                  review account connections in Settings.
                </p>
              )}
              {categories.map((c) => (
                <button
                  key={c.name}
                  className={`category-row ${category === c.name ? "chosen" : ""}`}
                  onClick={() =>
                    setCategory(category === c.name ? null : c.name)
                  }
                  aria-pressed={category === c.name}
                >
                  <div>
                    <span>
                      <i aria-hidden="true" className="category-marker" />
                      {c.name}
                    </span>
                    <strong>{formatMoney(c.total)}</strong>
                  </div>
                  <div className="category-track">
                    <i
                      style={{
                        width: `${Math.max(0, Math.min(100, (c.total / Math.max(1, ...categories.map((c) => c.total))) * 100))}%`,
                      }}
                    />
                  </div>
                  <small>
                    {c.count} transaction{c.count === 1 ? "" : "s"}
                  </small>
                </button>
              ))}
            </div>
            <div className="transaction-panel">
              <div className="agenda-heading">
                <h2>{category ?? "Transactions"}</h2>
                <span>{visible.length} items</span>
              </div>
              <label
                className="spending-search-label"
                htmlFor="spending-search"
              >
                Search transactions
              </label>
              <input
                id="spending-search"
                type="search"
                className="transaction-search"
                placeholder="Search merchants, accounts, categories…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {visible.length === 0 && (
                <div className="money-empty">
                  <p>No transactions match these filters.</p>
                  <button
                    onClick={() => {
                      setCategory(null);
                      setSearch("");
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              )}
              {visible.map((t) => (
                <div className="spending-transaction" key={t.id}>
                  <div>
                    <strong>{t.name}</strong>
                    <span>
                      {dateLabel(t.date)} · {t.account}
                      {t.pending ? " · Pending" : ""}
                    </span>
                    <small>{t.category}</small>
                  </div>
                  <strong className={t.amount < 0 ? "income" : ""}>
                    {t.amount < 0 ? "+" : ""}
                    {formatMoney(Math.abs(t.amount))}
                  </strong>
                </div>
              ))}
              <Link className="transaction-link" to="/transactions">
                Review and categorize transactions <ArrowUpRight size={14} />
              </Link>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
